import { FC, useCallback } from 'react';

import { Checkbox } from '@/components/form/Checkbox';
import { ResourceType } from '@/types';

import {
  areAllChildrenSelected,
  createVisibilityTree,
  handleParentToggle,
  NodesVisibilitySectionProps,
} from './types';

/**
 * NodesVisibilitySection - Hierarchical node visibility controls
 *
 * This section provides a tree-based interface for controlling which node types
 * and attributes are visible in the graph visualization. Supports parent-child
 * relationships where selecting a parent affects all its children.
 *
 * Features:
 * - Hierarchical checkbox tree structure
 * - Parent/child selection logic
 * - Entity type visibility (Vulnerabilities, Technologies, etc.)
 * - Attribute visibility with nested display
 * - Hide All / Show All bulk operations
 * - Responsive hover states
 *
 * @param attributes - Array of attribute names
 * @param visibilityState - Current visibility state for all nodes
 * @param onVisibilityStateChange - Callback to update visibility state
 */
export const NodesVisibilitySection: FC<NodesVisibilitySectionProps> = ({
  attributes,
  visibilityState,
  onVisibilityStateChange,
}) => {
  const handleAttributeToggle = useCallback(
    (attributeName: string) => {
      onVisibilityStateChange({
        ...visibilityState,
        [attributeName]: !visibilityState[attributeName],
      });
    },
    [visibilityState, onVisibilityStateChange]
  );

  const handleHideAll = useCallback(() => {
    const newState = { ...visibilityState };

    // Hide all entity types
    newState[ResourceType.Risk] = false;
    newState[ResourceType.Technology] = false;
    newState[ResourceType.Webpage] = false;
    newState[ResourceType.Seed] = false;
    newState.Tag = false;
    newState[ResourceType.Attribute] = false;

    // Hide all attributes
    attributes.forEach(name => {
      newState[name] = false;
    });

    onVisibilityStateChange(newState);
  }, [attributes, visibilityState, onVisibilityStateChange]);

  const handleShowAll = useCallback(() => {
    const newState = { ...visibilityState };

    // Show all entity types
    newState[ResourceType.Risk] = true;
    newState[ResourceType.Technology] = true;
    newState[ResourceType.Webpage] = true;
    newState[ResourceType.Seed] = true;
    newState.Tag = true;
    newState[ResourceType.Attribute] = true;

    // Show all attributes
    attributes.forEach(name => {
      newState[name] = true;
    });

    onVisibilityStateChange(newState);
  }, [attributes, visibilityState, onVisibilityStateChange]);

  return (
    <div className="flex flex-col gap-4 pr-2">
      {/* Visible Attributes Section */}
      <div className="flex flex-col gap-2">
        <h3 className="text-chariot-text-primary text-sm font-semibold">
          Visible Nodes
        </h3>
        {/* Add Hide All/Show All buttons */}
        <div className="flex gap-2">
          <button
            onClick={handleHideAll}
            className="text-chariot-text-secondary hover:text-chariot-text-primary text-xs transition-colors"
          >
            Hide all
          </button>
          <span className="text-chariot-text-secondary text-xs">•</span>
          <button
            onClick={handleShowAll}
            className="text-chariot-text-secondary hover:text-chariot-text-primary text-xs transition-colors"
          >
            Show all
          </button>
        </div>
        {/* Attribute Visibility Tree Section */}
        <div className="mt-2 flex flex-col gap-2">
          {createVisibilityTree(attributes).map(parentNode => (
            <div key={parentNode.id}>
              {/* Parent Row */}
              <div className="flex items-center gap-3 p-1">
                <Checkbox
                  name={parentNode.id}
                  label={parentNode.label}
                  value={
                    areAllChildrenSelected(parentNode, visibilityState)
                      ? 'true'
                      : 'false'
                  }
                  onChange={() =>
                    handleParentToggle(
                      parentNode,
                      visibilityState,
                      onVisibilityStateChange
                    )
                  }
                />
              </div>

              {/* Children Rows */}
              {parentNode.children && (
                <div className="flex flex-col">
                  {parentNode.children.map(childNode => {
                    const isVisible = visibilityState[childNode.id] ?? true;
                    return (
                      <div
                        key={childNode.id}
                        className="hover:bg-chariot-background-secondary/50 flex items-center gap-3 p-1 pl-6 last:border-b-0"
                      >
                        <Checkbox
                          name={childNode.id}
                          label={childNode.label}
                          value={isVisible ? 'true' : 'false'}
                          onChange={() => handleAttributeToggle(childNode.id)}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
